// Thin fetch wrapper.
// - Always sends cookies (`credentials: 'include'`) so the session flows.
// - Reads the XSRF-TOKEN cookie and echoes it as X-XSRF-TOKEN on mutating verbs.
// - Throws `ApiError` on non-2xx so callers can switch on status.

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly body: unknown,
  ) {
    super(`API ${status}`);
  }
}

function getCookie(name: string): string | undefined {
  return document.cookie
    .split('; ')
    .find((c) => c.startsWith(`${name}=`))
    ?.slice(name.length + 1);
}

const MUTATING = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

export async function api<T = unknown>(
  path: string,
  init: RequestInit = {},
): Promise<T> {
  const method = (init.method ?? 'GET').toUpperCase();
  const headers = new Headers(init.headers);

  if (init.body && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }
  if (MUTATING.has(method)) {
    const xsrf = getCookie('XSRF-TOKEN');
    if (xsrf) headers.set('X-XSRF-TOKEN', decodeURIComponent(xsrf));
  }

  const res = await fetch(path, { ...init, headers, credentials: 'include' });

  const text = await res.text();
  const body = text ? safeJson(text) : null;

  if (!res.ok) {
    // 401/403 on any non-auth /api path means our session is suspect (expired,
    // server restarted, CSRF cookie stale). Tell the AuthProvider to re-check
    // /api/auth/me. If still authenticated → user is just forbidden (e.g. guest
    // hitting OPERATE); the error propagates as ApiError and the page shows it
    // inline. If unauthenticated → user goes to null → RequireAuth redirects
    // to /login. The auth endpoints themselves are excluded to avoid loops.
    if (
      (res.status === 401 || res.status === 403) &&
      path.startsWith('/api/') &&
      !path.startsWith('/api/auth/')
    ) {
      window.dispatchEvent(new CustomEvent('auth:check'));
    }
    throw new ApiError(res.status, body);
  }
  return body as T;
}

function safeJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}
